import "@/index.css";
import { Dashboard } from "@/pages/Dashboard";
import _categories from "@/categories.json";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { addDays, format, formatRelative, parseISO } from "date-fns";
import { Combobox, ComboboxCollection, ComboboxContent, ComboboxEmpty, ComboboxGroup, ComboboxInput, ComboboxItem, ComboboxLabel, ComboboxList, ComboboxSeparator } from "@/components/ui/combobox";
import { ActivityList } from "@/components/ActivityList";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

export interface Category {
  label: string,
  value: string,
  items: Category[],
}

function buildCategories() {
  const roots = new Array<Category>();
  const _children = new Map<string, Category[]>();
  for (const category of _categories) {
    if (!category.parentSlug) {
      roots.push({
        label: category.name,
        value: category.slug,
        items: []
      });
      continue;
    }
    if (!_children.has(category.parentSlug)) {
      _children.set(category.parentSlug, []);
    }
    _children.get(category.parentSlug)!.push({
      label: category.name,
      value: category.slug,
      items: []
    });
  }
  for (const root of roots) {
    const q = [root];
    while (q.length) {
      const category = q.pop()!;
      if (!_children.has(category.value)) {
        root.items.push(category);
        continue;
      }
      for (const child of _children.get(category.value)!) {
        q.push(child);
      }
    }
  }
  return roots;
}

export const Categories = buildCategories();

export function Classes() {
  const { getUser, updateUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const dateParam = searchParams.get("date");
  const activityParam = searchParams.get("activity");

  useEffect(() => {
    (async () => {
      if (!dateParam) {
        searchParams.set("date", format(new Date(), "yyyy-MM-dd"));
      }
      if (!activityParam) {
        const user = await getUser();
        if (user?.lastActivity) {
          searchParams.set("activity", user.lastActivity);
        }
      }
      setSearchParams(searchParams, { replace: true });
    })();
  }, []);

  const date = useMemo(() => dateParam ? parseISO(dateParam) : undefined, [dateParam]);
  if (!date) {
    return null;
  }
  let dateFormat = formatRelative(date, new Date()).split(" at")[0]!;
  dateFormat = dateFormat[0]!.toUpperCase() + dateFormat.substring(1);
  return (
    <Dashboard pageName="Classes">
      <div className="flex flex-col gap-4 h-full">
        <div className="flex flex-wrap items-center gap-2 justify-between">
          <Combobox items={Categories} onValueChange={(activity: Category | null) => setSearchParams((prev) => {
            if (!activity) {
              prev.delete("activity");
            } else {
              updateUser({ lastActivity: activity.value });
              prev.set("activity", activity.value);
            }
            return prev;
          })} value={Categories.flatMap(cat => cat.items).find(cat => cat.value === activityParam)}>
            <ComboboxInput placeholder="Select an Activity" className="cursor-pointer" />
            <ComboboxContent>
              <ComboboxEmpty>No activities found.</ComboboxEmpty>
              <ComboboxList>
                {(group, index) => (
                  <ComboboxGroup key={index} items={group.items}>
                    <ComboboxLabel>{group.label}</ComboboxLabel>
                    <ComboboxCollection>
                      {(item, index) => (
                        <ComboboxItem key={index} value={item} className="cursor-pointer">
                          {item.label}
                        </ComboboxItem>
                      )}
                    </ComboboxCollection>
                    {index < Categories.length - 1 && <ComboboxSeparator />}
                  </ComboboxGroup>
                )}
              </ComboboxList>
            </ComboboxContent>
          </Combobox>
          <div className="flex gap-2">
            <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="data-[empty=true]:text-muted-foreground font-normal"
                >
                  <CalendarIcon />
                  {dateFormat}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end" sideOffset={8}>
                <Calendar mode="single" selected={date} onSelect={(date) => {
                  setSearchParams((prev) => {
                    prev.set("date", format(date, "yyyy-MM-dd"));
                    return prev;
                  });
                  setDatePickerOpen(false);
                }} required={true} />
              </PopoverContent>
            </Popover>
            <div className="flex gap-1">
              <Button variant="outline" onClick={() => setSearchParams((prev) => {
                prev.set("date", format(addDays(date, -1), "yyyy-MM-dd"));
                return prev;
              })}><ChevronLeft /></Button>
              <Button variant="outline" onClick={() => setSearchParams((prev) => {
                prev.set("date", format(addDays(date, 1), "yyyy-MM-dd"));
                return prev;
              })}><ChevronRight /></Button>
            </div>
          </div>
        </div>
        <ActivityList category={activityParam} date={date} />
      </div>
    </Dashboard >
  )
}
